import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { ServiceAccountAvatar } from "@/components/settings/organization/service-accounts/ServiceAccountAvatar";
import { Text } from "@/components/podkit/typography/Text";
import { useListServiceAccounts } from "@/queries/service-account-queries";
import { useMembers } from "@/queries/organization-queries";
import { Principal, type Subject } from "gitpod-next-api/gitpod/v1/identity_pb";
import { type PlainMessage } from "@bufbuild/protobuf";
import { useMemo, type FC } from "react";

type AutomationUserDisplayProps = {
    subject?: PlainMessage<Subject>;
};

export const AutomationUserDisplay: FC<AutomationUserDisplayProps> = ({ subject }) => {
    const { data: serviceAccountsData } = useListServiceAccounts();
    const { data: membersData } = useMembers();

    const userInfo = useMemo(() => {
        if (!subject) {
            return null;
        }

        if (subject.principal === Principal.USER) {
            const member = membersData?.members?.find((m) => m.userId === subject.id);
            if (member) {
                return {
                    name: member.fullName,
                    avatarUrl: member.avatarUrl,
                    principal: Principal.USER,
                };
            }
        }

        if (subject.principal === Principal.SERVICE_ACCOUNT) {
            const serviceAccount = serviceAccountsData?.serviceAccounts?.find((sa) => sa.id === subject.id);
            if (serviceAccount) {
                return {
                    name: serviceAccount.name,
                    principal: Principal.SERVICE_ACCOUNT,
                    id: serviceAccount.id,
                };
            }
        }

        return null;
    }, [subject, membersData, serviceAccountsData]);

    if (!userInfo) {
        return null;
    }

    return (
        <div className="flex items-center gap-1.5">
            {userInfo.principal === Principal.USER ? (
                <Avatar size={16}>
                    {userInfo.avatarUrl && <AvatarImage src={userInfo.avatarUrl} alt={`${userInfo.name}'s avatar`} />}
                    <AvatarFallback>
                        <AvatarInitials name={userInfo.name} size={16} />
                    </AvatarFallback>
                </Avatar>
            ) : userInfo.id ? (
                <ServiceAccountAvatar id={userInfo.id} size={16} />
            ) : null}
            <Text className="text-sm">{userInfo.name}</Text>
        </div>
    );
};
